/* eslint-disable @atlassian/relay/unused-fields */
import { type QueryOptions } from 'apollo-client';
import type ApolloClient from 'apollo-client';
import { type ContextId, type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fetchQuery, graphql, type Environment } from 'react-relay';

import type {
	GQLExtensionRenderingContextInput,
	UsePreFilteredExtensionsQueryOptions,
} from '../web-client';
import {
	createExtensionListQueryOptions,
	createUsePrefilteredExtensionListQueryOptions,
	getExtensionsFromGQLResult,
	getPrefilteredExtensionsGQLResult,
	type QueryData,
} from '../web-client';
import type { GraphQLError } from 'graphql';
import type {
	PreFilteredExtensionsQueryHookOptions,
	PrefilteredExtensionsResult,
} from '../web-client/hooks/useExtensionList';
import { FetchPreFilteredForgeAppsQuery } from '../web-client/hooks/lib/queries';
import type { ExtensionContextsFilter } from '../web-client/hooks/__generated__/useExtensionList_forge_ui_Query.graphql';
import { createAtlAttributionHeader } from '../utils';
import { type getForgeUIExtensionsAsync_Query as extensionListQuery } from './__generated__/getForgeUIExtensionsAsync_Query.graphql';
import { type getForgeUIExtensionsAsync_prefetchedQuery as prefetchedExtensionListQuery } from './__generated__/getForgeUIExtensionsAsync_prefetchedQuery.graphql';

export type GetForgeUIExtensionsAsyncOptions = {
	bypassNewForgeExtensionListCall?: boolean;
	client: ApolloClient<object>;
	cloudId?: string;
	context?: GQLExtensionRenderingContextInput;
	contextIds: ContextId[];
	dataClassificationTagIds?: string[];
	expandAppOwner?: boolean;
	extensionsFilter?: ExtensionContextsFilter[];
	includeHidden?: boolean;
	locale?: string;
	moduleType?: string;
	queryOptions?: Omit<QueryOptions, 'query'>;
	relayEnvironment?: Environment;
};

type ExtensionListResult = {
	errors?: ReadonlyArray<GraphQLError | Error>;
	extensions: ReadonlyArray<ForgeUIExtensionType> | null;
};

type PendingRequest = {
	expiresAt: number;
	promise: Promise<ExtensionListResult>;
};

/**
 * `fetchQuery` never reads the Relay store and only coalesces in-flight requests, so the
 * provider rebuilds spread across a page load each refetch the same extension list. The
 * TTL is what collapses them, so it has to outlive a page load without pinning a stale
 * list for the whole session.
 *
 * Kept per Relay environment so a rebuilt environment starts clean, and only used for the
 * prefiltered branch — the legacy branch serves bridge navigation lookups that run on user
 * action and must stay fresh.
 */
const PREFILTERED_REQUEST_TTL_MS = 60_000;

const prefilteredRequests = new WeakMap<Environment, Map<string, PendingRequest>>();

const readPrefilteredRequest = (
	environment: Environment,
	cacheKey: string,
): Promise<ExtensionListResult> | undefined => {
	const entry = prefilteredRequests.get(environment)?.get(cacheKey);

	if (!entry) {
		return undefined;
	}

	if (entry.expiresAt <= Date.now()) {
		prefilteredRequests.get(environment)?.delete(cacheKey);
		return undefined;
	}

	return entry.promise;
};

const writePrefilteredRequest = (
	environment: Environment,
	cacheKey: string,
	promise: Promise<ExtensionListResult>,
): Promise<ExtensionListResult> => {
	let requestsForEnvironment = prefilteredRequests.get(environment);

	if (!requestsForEnvironment) {
		requestsForEnvironment = new Map();
		prefilteredRequests.set(environment, requestsForEnvironment);
	}

	const requests = requestsForEnvironment;
	const entry: PendingRequest = {
		expiresAt: Date.now() + PREFILTERED_REQUEST_TTL_MS,
		promise,
	};
	requests.set(cacheKey, entry);

	// Only evict our own entry: one that expired while in flight has already been replaced.
	const evict = () => {
		if (requests.get(cacheKey) === entry) {
			requests.delete(cacheKey);
		}
	};

	promise.then((result) => {
		// A failed fetch resolves rather than rejects, so errors have to be read off the
		// result. On success the TTL restarts from when the data actually landed.
		if (result.errors?.length || result.extensions === null) {
			evict();
			return;
		}
		entry.expiresAt = Date.now() + PREFILTERED_REQUEST_TTL_MS;
	}, evict);

	return promise;
};

export const getForgeUIExtensionsAsync = async ({
	client,
	contextIds,
	moduleType,
	context,
	queryOptions,
	expandAppOwner,
	extensionsFilter,
	dataClassificationTagIds,
	locale,
	cloudId,
	includeHidden,
	bypassNewForgeExtensionListCall = false,
}: GetForgeUIExtensionsAsyncOptions): Promise<{
	errors?: ReadonlyArray<GraphQLError | Error>;
	extensions: ReadonlyArray<ForgeUIExtensionType> | null;
}> => {
	if (bypassNewForgeExtensionListCall === false) {
		const types: string[] = moduleType ? [moduleType] : [];

		const requestOptions =
			createUsePrefilteredExtensionListQueryOptions<PreFilteredExtensionsQueryHookOptions>(
				cloudId ?? contextIds[0],
				context ?? {},
				client,
				types,
				locale,
				includeHidden,
				queryOptions as UsePreFilteredExtensionsQueryOptions,
				contextIds,
			);

		const { fetchPolicy, ...rest } = requestOptions;

		const { loading, data, errors } = await client.query<PrefilteredExtensionsResult>({
			...rest,
			query: FetchPreFilteredForgeAppsQuery,
			fetchPolicy: fetchPolicy === 'cache-and-network' ? 'network-only' : fetchPolicy,
		});
		const extensions = getPrefilteredExtensionsGQLResult<ForgeUIExtensionType>({ loading, data });
		return {
			extensions,
			errors: errors,
		};
	} else {
		const requestOptions = createExtensionListQueryOptions<QueryOptions>(
			client,
			contextIds,
			queryOptions as QueryOptions,
			expandAppOwner,
			extensionsFilter,
			moduleType,
			dataClassificationTagIds,
			locale,
		);

		const queryResult = await client.query<QueryData>(requestOptions);
		return {
			extensions: getExtensionsFromGQLResult<ForgeUIExtensionType>(queryResult),
			errors: queryResult.errors,
		};
	}
};

export const getForgeUIExtensionsRelayAsync = async ({
	contextIds,
	moduleType,
	context,
	extensionsFilter,
	dataClassificationTagIds,
	locale,
	cloudId,
	includeHidden,
	bypassNewForgeExtensionListCall = false,
	relayEnvironment,
}: Omit<GetForgeUIExtensionsAsyncOptions, 'client'>): Promise<{
	errors?: ReadonlyArray<GraphQLError | Error>;
	extensions: ReadonlyArray<ForgeUIExtensionType> | null;
}> => {
	if (!relayEnvironment) {
		return {
			extensions: null,
			errors: [new Error('Relay environment is required for getForgeUIExtensionsRelayAsync')],
		};
	}

	if (!bypassNewForgeExtensionListCall) {
		const types: string[] = moduleType ? [moduleType] : [];
		const resolvedCloudId = cloudId ?? contextIds[0];

		const isRequestDedupeEnabled = isExperimentEnabled('platform_forge_extension_list_perf');
		// Must stay in step with the `fetchQuery` variables below.
		const cacheKey = isRequestDedupeEnabled
			? JSON.stringify({
					cloudId: resolvedCloudId,
					context: context ?? {},
					includeHidden,
					locale,
					types,
				})
			: '';

		if (isRequestDedupeEnabled) {
			const dedupedRequest = readPrefilteredRequest(relayEnvironment, cacheKey);

			if (dedupedRequest) {
				return dedupedRequest;
			}
		}

		const request = new Promise<ExtensionListResult>((resolve) => {
			fetchQuery<prefetchedExtensionListQuery>(
				relayEnvironment,
				graphql`
					query getForgeUIExtensionsAsync_prefetchedQuery(
						$cloudId: ID!
						$context: ConfluenceExtensionRenderingContextInput
						$includeHidden: Boolean
						$locale: String
						$types: [String]
					) {
						confluence_forgeExtensionsByType(
							cloudId: $cloudId
							context: $context
							includeHidden: $includeHidden
							locale: $locale
							types: $types
						) {
							id
							appId
							appVersion
							appOwner
							environmentId
							environmentType
							environmentKey
							consentUrl
							migrationKey
							userAccess {
								enabled
								hasAccess
							}
							key
							properties {
								key
								value
							}
							egress {
								addresses
								category
								type
								inScopeEUD
							}
							sandboxPolicies
							scopes
							license {
								active
								capabilitySet
								billingPeriod
								ccpEntitlementId
								ccpEntitlementSlug
								isEvaluation
								subscriptionEndDate
								trialEndDate
								type
							}
							type
							installationId
							hiddenBy
						}
					}
				`,
				{
					cloudId: resolvedCloudId,
					// @ts-ignore - the generated types convert GQL Long to a number, which may truncate the id. The type will be changed to string
					context: context ?? {},
					includeHidden,
					locale,
					types,
				},
				{
					networkCacheConfig: {
						metadata: {
							headers: {
								'atl-attribution': createAtlAttributionHeader(cloudId),
							},
							allowOnExternalPage: true,
						},
					},
				},
			).subscribe({
				next: (data) => {
					const extensions = getPrefilteredExtensionsGQLResult({
						data,
						loading: false,
					});

					resolve({ extensions });
				},
				error: (error: Error) => {
					resolve({ errors: [error], extensions: null });
				},
			});
		});

		return isRequestDedupeEnabled
			? writePrefilteredRequest(relayEnvironment, cacheKey, request)
			: request;
	}

	const filter = extensionsFilter ?? [
		{
			type: 'EXTENSION_TYPE' as const,
			value: [moduleType] as readonly string[],
		},
	];

	return new Promise((resolve) => {
		fetchQuery<extensionListQuery>(
			relayEnvironment,
			graphql`
				query getForgeUIExtensionsAsync_Query(
					$contextIds: [ID!]!
					$filter: [ExtensionContextsFilter!]!
					$locale: String
					$dataClassificationTags: [ID!]!
				) {
					extensionContexts(contextIds: $contextIds) {
						id
						extensions(filter: $filter, locale: $locale) @optIn(to: "Access-Narrowing") {
							id
							appId
							key
							appOwner {
								name
							}
							environmentId
							environmentType
							environmentKey
							properties
							license {
								active
								type
								supportEntitlementNumber
								trialEndDate
								subscriptionEndDate
								isEvaluation
								billingPeriod
								ccpEntitlementId
								ccpEntitlementSlug
								capabilitySet
								modes
							}
							userAccess {
								enabled
								hasAccess
							}
							type
							appVersion
							installationId
							migrationKey
							installationConfig {
								key
								value
							}
							egress {
								type
								addresses
								category
								inScopeEUD
							}
							sandboxPolicies
							scopes
							consentUrl
							currentUserConsent {
								user {
									aaid
								}
								appEnvironmentVersion {
									id
								}
								consentedAt
							}
							requiresUserConsent
							dataClassificationPolicyDecision(
								input: { dataClassificationTags: $dataClassificationTags }
							) {
								status
							}
						}
					}
				}
			`,
			{
				contextIds,
				locale,
				filter,
				dataClassificationTags: dataClassificationTagIds || [],
			},
			{
				networkCacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader([...contextIds].sort().join()),
						},
					},
				},
			},
		).subscribe({
			next: (data) => {
				const extensions = getExtensionsFromGQLResult<ForgeUIExtensionType>({
					data,
					loading: false,
				});

				resolve({ extensions });
			},
			error: (error: Error) => {
				resolve({ errors: [error], extensions: null });
			},
		});
	});
};
